/**
 * Strip the trailing "11" suffix from a ticker param. Public tickers
 * on the site are always rendered as "KNCA11" but the underlying
 * Prisma `idCem` column is "KNCA". Centralized here so the route
 * pages and the page-load helper agree on the format.
 */
export const normalizeInstrument = (value?: string | null) => {
  const trimmed = (value ?? '').trim().toUpperCase();
  // Treasury identifiers may legitimately contain numeric components. Preserve
  // their type/date identity, while migrating the legacy route/database form.
  const legacyTreasury = trimmed.match(/^(TESOURO_[^:]+):(\d{4}-\d{2}-\d{2})$/);
  if (legacyTreasury) return `${legacyTreasury[1]}-${legacyTreasury[2]}`;
  if (/^TESOURO_(?:.+-)?\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  if (trimmed === '11') return trimmed;
  return trimmed.endsWith('11') ? trimmed.slice(0, -2) : trimmed;
};

export const isTreasuryInstrument = (value?: string | null) =>
  (value ?? '').trim().toUpperCase().startsWith('TESOURO_');

/**
 * Decode one percent-encoded URI component coming from a route segment.
 * The `[ticker]` segment of `/ativo/{ticker}/...` arrives encoded, so the
 * legacy colon inside Treasury identifiers shows up as `%3A` and must be
 * decoded before the value is used for lookups. Already-decoded values are left
 * untouched (never double-encode), and malformed encodings fall back to
 * the original value instead of throwing.
 */
export const decodeInstrumentRouteParam = (value?: string | null) => {
  const raw = value ?? '';
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

export const displayInstrument = (value: string, fundType?: string | null) => {
  const normalized = normalizeInstrument(value);
  if ((fundType ?? '').toUpperCase() === 'TESOURO' || isTreasuryInstrument(value)) return normalized;
  return normalized.endsWith('11') ? normalized : `${normalized}11`;
};
